import type React from "react"

import type { SoftReceivedLotReference } from "./subfeatures/SoftReceived/types"

import type {
  GoodsReceipt,
  GoodsReceiptPallet,
  GoodsReceiptPalletRow,
  GoodsReceiptProductDetail,
  GrLineCostBreakdown,
  OrderLine,
  WarehouseType,
} from "@/graphql/codegen/graphql"

export interface GoodsReceiptFilterFormValues {
  warehouseAreaIds: string[]
}

export interface WarehouseContext {
  name?: string
  id?: string
  type?: WarehouseType | null
}

export enum GoodReceiptDialogType {
  PurchaseOrderSelection = "PURCHASE_ORDER_SELECTION",
  GoodsReceipt = "GOODS_RECEIPT",
  CancelConfirmation = "CANCEL_CONFIRMATION",
}

export enum GoodReceiptActionType {
  Form = "FORM",
  Delete = "DELETE",
  AddPurchaseOrder = "ADD_PURCHASE_ORDER",
  SoftReceive = "SOFT_RECEIVE",
  MarkAsNotReceived = "MARK_AS_NOT_RECEIVED",
  FileUpload = "FILE_UPLOAD",
  SoftReceiveSuccess = "SOFT_RECEIVE_SUCCESS",
  FinalizeCheckInUntestedLots = "FINALIZE_CHECK_IN_UNTESTED_LOTS",
  FinalizeCheckInConfirmPalletsCount = "FINALIZE_CHECK_IN_CONFIRM_PALLETS_COUNT",
}

export type GoodsReceiptActionState =
  | { type: GoodReceiptActionType.Form; goodsReceipt: GoodsReceipt | null }
  | { type: GoodReceiptActionType.Delete; goodsReceipt: GoodsReceipt }
  | { type: GoodReceiptActionType.AddPurchaseOrder }
  | { type: GoodReceiptActionType.SoftReceive }
  | { type: GoodReceiptActionType.MarkAsNotReceived; goodsReceipt: GoodsReceipt }
  | { type: GoodReceiptActionType.FileUpload }
  | { type: GoodReceiptActionType.SoftReceiveSuccess }
  | { type: GoodReceiptActionType.FinalizeCheckInUntestedLots; untestedLots: SoftReceivedLotReference[] }
  | { type: GoodReceiptActionType.FinalizeCheckInConfirmPalletsCount }
  | null

export enum GoodsReceiptPalletActionType {
  Create = "CREATE",
  Delete = "DELETE",
  EditDestination = "EDIT_DESTINATION",
  EditLpn = "EDIT_LPN",
}

export type GoodsReceiptPalletActionState =
  | { type: GoodsReceiptPalletActionType.Create }
  | { type: GoodsReceiptPalletActionType.EditDestination; pallet: GoodsReceiptPallet }
  | { type: GoodsReceiptPalletActionType.EditLpn; pallet: GoodsReceiptPallet }
  | { type: GoodsReceiptPalletActionType.Delete; pallet: GoodsReceiptPallet }
  | null

export enum GoodsReceiptPalletDeleteScenario {
  Standard = "STANDARD",
  Last = "LAST",
  LastNonStored = "LAST_NON_STORED",
}

export interface GoodsReceiptTotals {
  palletsCount: number
  unitsCount: number
  quantity: number | null
  uomId: string
}

export interface PurchaseOrderLineWithReceiptContext extends OrderLine {
  containerTrackingNumbers: string[]
  goodsReceiptLine: GoodsReceiptProductDetail | null
  palletRows: GoodsReceiptPalletRow[]
  costBreakdown: GrLineCostBreakdown | null
}

export interface PurchaseOrderLineQuantity {
  palletsCount: number
  regional: {
    us: { quantity: number; uomId: string }
    metric: { quantity: number; uomId: string }
  }
}

/**
 * Column keys that can be displayed in the GoodsReceipts table
 */
export type GoodsReceiptColumnKey =
  | "agrNumber"
  | "grNumber"
  | "expectedDate"
  | "softReceivedOn"
  | "checkedInOn"
  | "trackingNumbers"
  | "supplierName"
  | "holdLocation"
  | "warehouse"
  | "actions"

export interface GoodsReceiptDetailsTabConfig {
  path: string
  label: string
  isHidden?: boolean
}

export interface GoodsReceiptHeaderActionConfig {
  label: string
  testId: string
  icon: React.ReactNode
  handler: () => void
  isHidden?: boolean
}

export type GoodsReceiptHeaderActionsByStatus = Record<string, GoodsReceiptHeaderActionConfig[]>
